import { Injectable } from '@angular/core'
import { BehaviorSubject, forkJoin } from 'rxjs'
import { map, switchMap } from 'rxjs/operators'
import { BuilderService } from 'src/app/builder-services/builder.service'
import { AppSettingsService } from '../settings/services/app-settings.service'

export interface Vendor {
  id: string
  activation: boolean
  img: string
  name: string
  creationDate: string
  email?: string
  orders?: any
  total?: any
  currency?: string
}

@Injectable({ providedIn: 'root' })
export class VendorService {
  private vendorsState = new BehaviorSubject<Vendor[]>([])
  vendors = this.vendorsState.asObservable()
  // store the currently selected vendor
  private hasLoaded = false
  defaultLimit = 20
  vendorLimitState = new BehaviorSubject<number>(this.defaultLimit) // default fallback
  vendorLimit = this.vendorLimitState.asObservable()
  superVendorId: string | null = null
  isMultiVendor: boolean = false
  isVendor: boolean = false
  constructor(private builder: BuilderService, private appSettingsService: AppSettingsService) {
    this.appSettingsService.getGeneralSettings(this.builder.selectedProject).subscribe((settings) => {
      if (settings?.project_type === 'multi-vendor') {
        this.isMultiVendor = true
        localStorage.setItem('superVendor', this.builder.selectedProject)
      } else if (settings?.project_type === 'vendor') {
        this.isVendor = true
      } else {
        this.isVendor = false
        this.isMultiVendor = false
        localStorage.removeItem('superVendor')
      }
    })
    this.loadVendorsOnce()
  }

  syncSuperVendorSettingsToVendors(): void {
    this.builder.showUpdateProgress = false
  }

  /** Call ONCE from AppComponent */
  loadVendorsOnce(): void {
    if (this.hasLoaded) {
      return
    }
    this.hasLoaded = true
    this.loadVendors()
  }

  /** Refresh manually if needed */
  refresh(superVendor?: string): void {
    this.loadVendors(superVendor)
  }

  private loadVendorLimit(): void {
    this.vendorLimitState.next(this.defaultLimit)
  }
  /** derived helper: can we add more vendors? */
  canAddMoreVendors(): boolean {
    return this.vendorsList.length < this.vendorLimitState.value
  }
  private makeVendor(vendorId: string, stored: any, settings?: any): Vendor {
    return {
      id: vendorId,
      activation: stored.activation !== undefined ? stored.activation : true,
      email: stored.email || '',
      creationDate: stored.creationDate || new Date().toISOString(),
      img: settings.icon || '',
      name: settings.application_name || vendorId
    }
  }

  private loadVendors(_superVendor?: string): void {
    this.vendorsState.next([])
  }
  get vendorsList(): Vendor[] {
    return this.vendorsState.value
  }

  getActiveVendors(): Vendor[] {
    const list = this.vendorsState.value || []
    const active: Vendor[] = []
    for (let i = 0; i < list.length; i++) {
      const v = list[i]
      if (v && v.activation === true) {
        active.push(v)
      }
    }
    return active
  }
/**
 * Calculates order counts and revenue totals for a set of vendors (projects) over an optional date range,
 * enriching each vendor with computed fields and returning overall aggregates.
 *
 * ## What it does
 * - For each `vendor`, loads that project's `users` and `settings` from:
 *   - `projects/{vendor.id}/users`
 *   - `projects/{vendor.id}/settings`
 * - Walks all users' orders and **keeps only** the orders that:
 *   1) belong to the current project (`order.project === vendor.id`), and
 *   2) have a parsable `order.date` in **`dd-MM-yyyy`** format, and
 *   3) fall within the optional `[startDate, endDate]` range (both bounds **inclusive**).
 * @param {Vendor[]} vendors
 * An array of vendors (projects) to compute metrics for. Each vendor 
 * @param {Date | null} [startDate]
 * Optional inclusive lower bound. Orders with `orderDate < startDate` are excluded. If omitted/null, no lower bound.
 *
 * @param {Date | null} [endDate]
 * Optional inclusive upper bound. Orders with `orderDate > endDate` are excluded. If omitted/null, no upper bound.
 *
 */

  calculateProjectRevenues(vendors: Vendor[], startDate?: Date | null, endDate?: Date | null) {
    let totalOrders = 0
    let totalRevenue = 0

    const vendorRequests = vendors.map((vendor) =>
      this.appSettingsService.getGeneralSettings(vendor.id).pipe(
        map((settings: any) => {
          const currency = settings?.currency || 'USD'
          vendor.orders = '0'
          vendor.total = '0.00 ' + currency
          vendor.currency = currency
          return vendor
        })
      )
    )

    return forkJoin(vendorRequests).pipe(
      map((updatedVendors: Vendor[]) => ({
        vendors: updatedVendors,
        totalOrders,
        totalRevenue
      }))
    )
  }
}
